import { PDFDocument, PageBoundingBox } from "pdf-lib";

/**
 * Parses a page range string (e.g. "1,3,5-7,10") into a Set of 0-based page indices.
 * Throws an error with descriptive messages if validation fails.
 * @param specString The range specification string
 * @param totalPages The total number of pages in the PDF
 */
export function parsePageSpecification(specString: string, totalPages: number): Set<number> {
  const indices = new Set<number>();
  if (!specString || !specString.trim()) {
    return indices;
  }

  const parts = specString.split(",");
  for (let part of parts) {
    part = part.strip ? part.strip() : part.trim();
    if (!part) {
      continue;
    }

    if (part.includes("-")) {
      const rangeParts = part.split("-");
      if (rangeParts.length !== 2) {
        throw new Error(`Invalid range format: '${part}'`);
      }
      const start = parseInt(rangeParts[0].trim(), 10);
      const end = parseInt(rangeParts[1].trim(), 10);

      if (isNaN(start) || isNaN(end)) {
        throw new Error(`Invalid numbers in range: '${part}'`);
      }
      if (start <= 0 || end <= 0) {
        throw new Error(`Page numbers must be positive: '${part}'`);
      }
      if (start > end) {
        throw new Error(`Start page cannot be greater than end page: '${part}'`);
      }
      if (end > totalPages) {
        throw new Error(`Page range '${part}' exceeds total pages (${totalPages})`);
      }

      for (let i = start - 1; i < end; i++) {
        indices.add(i);
      }
    } else {
      const pageNum = parseInt(part, 10);
      if (isNaN(pageNum)) {
        throw new Error(`Invalid page number: '${part}'`);
      }
      if (pageNum <= 0) {
        throw new Error(`Page numbers must be positive: '${part}'`);
      }
      if (pageNum > totalPages) {
        throw new Error(`Page number '${part}' exceeds total pages (${totalPages})`);
      }
      indices.add(pageNum - 1);
    }
  }

  return indices;
}

/**
 * Formats a Set of 0-based page indices into a clean specification string.
 * E.g. [0, 1, 2, 4] -> "1-3,5"
 */
export function formatPageSpecification(indicesSet: Set<number>): string {
  const sorted = Array.from(indicesSet).sort((a, b) => a - b);
  if (sorted.length === 0) return "";

  const pages = sorted.map(i => i + 1);
  const parts: string[] = [];
  let start = pages[0];
  let prev = pages[0];

  for (let i = 1; i <= pages.length; i++) {
    const curr = pages[i];
    if (curr === prev + 1) {
      prev = curr;
    } else {
      if (start === prev) {
        parts.push(`${start}`);
      } else {
        parts.push(`${start}-${prev}`);
      }
      start = curr;
      prev = curr;
    }
  }

  return parts.join(",");
}

export interface StitchOptions {
  exclusionSpec: string;
  side: "left" | "right";
  splitRatio?: number;   // Value between 0.1 and 0.9 (default: 0.5)
  outerMargin?: number;  // Padding on left/right outer borders of destination page
  gutter?: number;       // Padding between the two columns
  tbPadding?: number;    // Top/bottom padding
  leftPadding?: number;  // Visual horizontal offset adjustment for left column (default: 0)
  rightPadding?: number; // Visual horizontal offset adjustment for right column (default: 0)
  onProgress?: (message: string) => void;
}

interface CropBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Helper to convert visual coordinates on a rotated page back to physical coordinates.
 * Supports 0, 90, 180, and 270 degree rotations.
 */
function visualToPhysical(
  visualRect: CropBox,
  pageWidth: number, // physical width W
  pageHeight: number, // physical height H
  rotation: number
): CropBox {
  const angle = ((rotation % 360) + 360) % 360;
  const { x, y, width: w, height: h } = visualRect;

  if (angle === 0) {
    return { x: x, y: y, width: w, height: h };
  } else if (angle === 90) {
    return { x: y, y: x, width: h, height: w };
  } else if (angle === 180) {
    return { x: pageWidth - x - w, y: pageHeight - y - h, width: w, height: h };
  } else if (angle === 270) {
    return { x: pageWidth - y - h, y: pageHeight - x - w, width: h, height: w };
  }
  return { x: x, y: y, width: w, height: h };
}

/**
 * Calculates the physical, unrotated crop box bounds for the source page.
 * Takes page rotation, cropBox/mediaBox, and splitRatio into account.
 * Returns a PageBoundingBox { left, right, bottom, top } format that pdf-lib expects.
 */
function getPhysicalBoundingBox(
  box: CropBox,
  rotation: number,
  side: "left" | "right",
  splitRatio: number
): PageBoundingBox {
  const { width: W, height: H } = box;

  // Calculate visual page dimensions based on rotation
  const angle = ((rotation % 360) + 360) % 360;
  const W_visual = (angle === 90 || angle === 270) ? H : W;
  const H_visual = (angle === 90 || angle === 270) ? W : H;

  // Define visual crop area relative to visual page size
  const visualCropRect: CropBox = {
    x: side === "left" ? 0 : W_visual * splitRatio,
    y: 0,
    width: side === "left" ? W_visual * splitRatio : W_visual * (1 - splitRatio),
    height: H_visual,
  };

  // Convert visual crop box back to physical coordinates
  const physicalRect = visualToPhysical(visualCropRect, W, H, rotation);

  return {
    left: box.x + physicalRect.x,
    right: box.x + physicalRect.x + physicalRect.width,
    bottom: box.y + physicalRect.y,
    top: box.y + physicalRect.y + physicalRect.height,
  };
}

/**
 * Calculates the drawing destination rectangle relative to the destination page.
 * Respects splitRatio, outerMargin, gutter, and tbPadding.
 * Prevents stretching by calculating scaling uniformly to fit both columns on the page.
 * Equally distributes remaining whitespace on left and right sides by centering the content.
 */
function getDestinationDrawRect(
  box: CropBox,
  rotation: number,
  slot: "left" | "right",
  side: "left" | "right",
  splitRatio: number,
  outerMargin: number,
  gutter: number,
  tbPadding: number,
  leftPadding: number,
  rightPadding: number
): CropBox {
  const { width: W, height: H } = box;

  // Calculate visual page dimensions of the destination page
  const angle = ((rotation % 360) + 360) % 360;
  const W_visual = (angle === 90 || angle === 270) ? H : W;
  const H_visual = (angle === 90 || angle === 270) ? W : H;

  // Calculate visual dimensions of the cropped column source
  const wCrop_visual = side === "left" ? W_visual * splitRatio : W_visual * (1 - splitRatio);
  const hCrop_visual = H_visual;

  // Compute scale factors to fit two columns + gutter + outerMargins on the page
  const Sw = (W_visual - 2 * outerMargin - gutter) / (2 * wCrop_visual);
  const Sh = (H_visual - 2 * tbPadding) / hCrop_visual;

  // Scale factor S is uniform to prevent text stretching, capped at 1.0 (no upscaling)
  const S = Math.max(0, Math.min(1.0, Sw, Sh));

  const wDraw_visual = wCrop_visual * S;
  const hDraw_visual = hCrop_visual * S;

  // Center the 2-column block as a whole group on the visual page width
  const wContent_visual = 2 * wDraw_visual + gutter;
  const dx = (W_visual - wContent_visual) / 2;

  // Center vertically
  const dy = (H_visual - hDraw_visual) / 2;

  // Determine drawing coordinates in visual space, applying leftPadding / rightPadding to nudge columns
  const visualDrawRect: CropBox = {
    x: slot === "left" ? dx + leftPadding : dx + wDraw_visual + gutter - rightPadding,
    y: dy,
    width: wDraw_visual,
    height: hDraw_visual,
  };

  // Convert visual coordinates to physical coordinates
  return visualToPhysical(visualDrawRect, W, H, rotation);
}

/**
 * Loads a PDF file from bytes, crops the specified side (left or right) of valid pages,
 * and stitches consecutive pages side-by-side onto a new page.
 * Keeps the original PDF text selectable and unchanged.
 */
export async function stitchPdfHalves(
  pdfBytes: Uint8Array,
  options: StitchOptions
): Promise<Uint8Array> {
  const {
    exclusionSpec,
    side,
    splitRatio = 0.5,
    outerMargin = 0,
    gutter = 0,
    tbPadding = 0,
    leftPadding = 0,
    rightPadding = 0,
    onProgress,
  } = options;
  const logger = onProgress || ((msg: string) => console.log(msg));

  logger(`Loading PDF document...`);
  const srcDoc = await PDFDocument.load(pdfBytes);
  const totalPages = srcDoc.getPageCount();
  logger(`Loaded PDF with ${totalPages} pages.`);

  logger(`Parsing exclusion specification: "${exclusionSpec}"...`);
  const excludedIndices = parsePageSpecification(exclusionSpec, totalPages);
  logger(`Excluded 0-based indices count: ${excludedIndices.size}`);

  const validIndices: number[] = [];
  for (let i = 0; i < totalPages; i++) {
    if (!excludedIndices.has(i)) {
      validIndices.push(i);
    }
  }
  const numValidPages = validIndices.length;
  logger(`Processing ${numValidPages} valid pages after exclusions.`);

  if (numValidPages === 0) {
    logger(`No pages left to process. Creating empty PDF.`);
    const newDoc = await PDFDocument.create();
    return await newDoc.save({ useObjectStreams: true });
  }

  const destDoc = await PDFDocument.create();
  const srcPages = srcDoc.getPages();

  for (let j = 0; j < numValidPages; j += 2) {
    const leftOrigIdx = validIndices[j];
    const rightOrigIdx = j + 1 < numValidPages ? validIndices[j + 1] : null;

    logger(`Stitching page pair: Left: #${leftOrigIdx + 1} (${j + 1}/${numValidPages})${rightOrigIdx !== null ? `, Right: #${rightOrigIdx + 1} (${j + 2}/${numValidPages})` : ""}`);

    const pageLeft = srcPages[leftOrigIdx];
    const rotationLeft = pageLeft.getRotation().angle;
    const cropBoxLeft = pageLeft.getCropBox() || pageLeft.getMediaBox();
    
    // Get physical bounding box using PageBoundingBox format
    const physicalBBoxLeft = getPhysicalBoundingBox(cropBoxLeft, rotationLeft, side, splitRatio);
    
    // Embed the cropped left page using pdf-lib's boundingBox parameter
    const embeddedLeft = await destDoc.embedPage(pageLeft, physicalBBoxLeft);

    // Create target page matching the source page physical size and rotation
    const newPage = destDoc.addPage([cropBoxLeft.width, cropBoxLeft.height]);
    newPage.setRotation(pageLeft.getRotation());

    // Get the destination drawing rect on the destination page
    const drawRectLeft = getDestinationDrawRect(
      cropBoxLeft,
      rotationLeft,
      "left",
      side,
      splitRatio,
      outerMargin,
      gutter,
      tbPadding,
      leftPadding,
      rightPadding
    );

    // Draw the left embedded page onto the new page
    newPage.drawPage(embeddedLeft, drawRectLeft);

    // Draw right page if available
    if (rightOrigIdx !== null) {
      const pageRight = srcPages[rightOrigIdx];
      const rotationRight = pageRight.getRotation().angle;
      const cropBoxRight = pageRight.getCropBox() || pageRight.getMediaBox();
      
      // Calculate physical bounding box for right page
      const physicalBBoxRight = getPhysicalBoundingBox(cropBoxRight, rotationRight, side, splitRatio);
      
      // Embed cropped right page passing the boundingBox parameter
      const embeddedRight = await destDoc.embedPage(pageRight, physicalBBoxRight);

      // Get destination drawing rect for right slot
      const drawRectRight = getDestinationDrawRect(
        cropBoxRight,
        rotationRight,
        "right",
        side,
        splitRatio,
        outerMargin,
        gutter,
        tbPadding,
        leftPadding,
        rightPadding
      );

      // Draw right page in right slot
      newPage.drawPage(embeddedRight, drawRectRight);
    }
  }

  logger(`Saving PDF and compressing (using Flate compress)...`);
  const finalBytes = await destDoc.save({ useObjectStreams: true });
  logger(`Stitching completed successfully!`);

  return finalBytes;
}
